import { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { format } from 'date-fns';
import { AppContext, SttDialog, SttList } from '../all';
import { useCrud, useInteractionsFiles } from '../../lib/hooks';
import SigCapture from '../../components/signature/SigCapture';
import { fullName } from '../../lib/utils';

type User = {
    id: number;
    name?: string;
    surname?: string;
};

type Template = {
    id: number;
    name: string;
    text?: string;
    textShort?: string;
};

type File = {
    id: number;
};

type Consent = {
    template: Template;
    file?: File;
};

type TemplatesResponse = {
    rows: Template[];
};

type FileResponse = {
    file?: File;
};

type Props = {
    user: User;
    consents: Consent[];
    onDelete?: (consent: Consent) => void;
    onSign?: () => void;
    onClose?: () => void;
};

/**
 * SttConsentList - list of user-accepted consent
 * @param consents
 * @param onAdd
 * @param onDelete
 */
export function SttSelectConsentList({
    user,
    consents,
    onSign,
    onClose,
}: Props): React.ReactElement {
    const { uploadFile, downloadFile } = useInteractionsFiles();
    const [addModal, setAddModal] = useState(false);
    const [signing, setSigning] = useState<Template | null>(null);
    const [templates, setTemplates, , Get] = useCrud<Template[]>([]);

    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    useEffect(() => {
        loadTemplates();
    }, []);

    function loadTemplates() {
        Get<TemplatesResponse>(`/scs/${cxt?.sc?.id}/consents/templates`, {
            rows: 1000,
        }).then((response) => {
            setTemplates(response.rows);
        });
    }

    function selected(consent: Consent) {
        if (consent.file?.id) {
            downloadFile(
                `/files/${consent.file.id}.pdf`,
                'application/pdf',
                undefined,
                `${t('Consent')} ${fullName(user)} - ${
                    consent.template.name
                }.pdf`,
            );
        }
    }

    function selectTemplate(template: Template) {
        setSigning(template);
        setAddModal(false);
    }

    function finish() {
        onSign?.();
        onClose?.();
    }

    function captureImage(dataUrl: string) {
        fetch(dataUrl)
            .then((response: any) => response.blob())
            .then(
                (blob) => new File([blob], 'temp.jpg', { type: 'image/jpeg' }),
            )
            .then((file: any) => {
                uploadFile(
                    {
                        name: `Firma_${user.name}_${format(new Date(), 'dd-MM-yyyy')}`,
                        type: 'SIGNATURE',
                        format: 'image/jpeg',
                        idUser: user.id,
                        idTemplate: signing!.id,
                    } as { name: string; type: string; format: string },
                    file,
                )
                    .then((response) => {
                        const fileResponse = response as FileResponse;
                        const { file } = fileResponse;
                        if (file?.id) {
                            finish();
                        } else {
                            console.log("No 'file' en la respuesta.");
                        }
                    })
                    .catch(console.log);
            });
    }

    function closeModal() {
        setAddModal(false);
    }

    return (
        <>
            {signing ? (
                <Box display="flex" justifyContent="center" alignItems="center">
                    <SigCapture
                        text={
                            signing.textShort
                                ? signing.textShort
                                : signing.text || ''
                        }
                        width={300}
                        height={200}
                        onCapture={(dataUrl: string) => captureImage(dataUrl)}
                    />
                </Box>
            ) : (
                <SttList<Consent>
                    title={t('Consents')}
                    data={(consents || []).map((consent) => ({
                        caption: consent.template.name,
                        objToSelect: consent,
                    }))}
                    onAdd={() => setAddModal(true)}
                    onSelect={(consent: Consent) => selected(consent)}
                />
            )}
            <SttDialog
                open={addModal}
                onClose={closeModal}
                title={t('selectConsentTemplate')}
                content={
                    <SttList<Template>
                        paper
                        title={t('consentTemplates')}
                        data={(templates || []).map((template) => ({
                            caption: template.name,
                            objToSelect: template,
                        }))}
                        onSelect={(template: Template) =>
                            selectTemplate(template)
                        }
                    />
                }
                buttons={[
                    {
                        type: 'close',
                        onClick: closeModal,
                    },
                ]}
            />
        </>
    );
}
